(function () {
  /**
   * MulticolumnManager - Uses flexbox space-between for layout
   */
  class MulticolumnManager {
    constructor(element) {
      this.container = element;
      this.wrapper = element.querySelector('.multicolumn-list__wrapper');
      this.allColumns = Array.from(element.querySelectorAll('.multicolumn-list__item'));
      
      this.rotationDelay = parseInt(element.dataset.autoplayDelay) || 4000;
      this.autoplayEnabled = element.dataset.autoplayEnabled === 'true';
      
      this.visibleStartIndex = 0;
      this.visibleCount = 0;
      this.totalColumns = this.allColumns.length;
      this.rotationTimer = null;
      this.isAnimating = false;
      this.lastContainerWidth = 0;
      
      this.columnData = this.allColumns.map((col, index) => ({
        element: col.cloneNode(true),
        width: parseInt(col.dataset.columnWidth) || 280,
        index: index
      }));
      
      this.allColumns.forEach(col => col.style.display = 'none');
      
      this.init();
    }
    
    calculateVisibleCount() {
      // Get the actual usable width - wrapper.clientWidth already excludes padding
      const containerWidth = this.wrapper.clientWidth || this.container.clientWidth;
      const minGap = 48; // Gap between columns (divider + spacing on each side)
      let totalWidth = 0;
      let count = 0;
      
      for (let i = 0; i < this.totalColumns; i++) {
        const colIndex = (this.visibleStartIndex + i) % this.totalColumns;
        const colWidth = this.columnData[colIndex].width;
        
        // Calculate total width if we add this column
        // First column: just the column width
        // Additional columns: column width + gap (which includes divider)
        const additionalWidth = count > 0 ? colWidth + minGap : colWidth;
        const newTotal = totalWidth + additionalWidth;
        
        // Use generous safety margin to prevent cutoff
        // Account for potential rounding and space-between distribution
        if (newTotal + 60 <= containerWidth) {
          totalWidth = newTotal;
          count++;
        } else {
          break;
        }
      }
      
      return Math.max(1, count);
    }
    
    createColumn(colData) {
      const col = colData.element.cloneNode(true);
      col.style.display = 'flex';
      col.style.width = `${colData.width}px`;
      col.style.flexShrink = '0';
      col.classList.add('multicolumn-list__item--visible');
      
      const card = col.querySelector('.multicolumn-card');
      if (card) {
        card.classList.toggle('multicolumn-card--wide', colData.width >= 400);
        card.classList.toggle('multicolumn-card--narrow', colData.width < 400);
      }
      
      return col;
    }
    
    createDivider() {
      const divider = document.createElement('div');
      divider.className = 'multicolumn-divider';
      divider.style.width = '1px';
      divider.style.alignSelf = 'stretch';
      divider.style.backgroundColor = 'rgb(var(--color-border))';
      divider.style.flexShrink = '0';
      return divider;
    }
    
    render() {
      this.visibleCount = this.calculateVisibleCount();
      
      this.wrapper.innerHTML = '';
      this.wrapper.style.display = 'flex';
      this.wrapper.style.flexDirection = 'row';
      // Use center for single column, space-evenly for multiple (more balanced than space-between)
      this.wrapper.style.justifyContent = this.visibleCount === 1 ? 'center' : 'space-evenly';
      this.wrapper.style.alignItems = 'stretch';
      this.wrapper.style.width = '100%';
      this.wrapper.style.maxWidth = '100%';
      this.wrapper.style.overflow = 'hidden';
      this.wrapper.style.position = 'relative';
      this.wrapper.style.boxSizing = 'border-box';
      
      for (let slot = 0; slot < this.visibleCount; slot++) {
        const colIndex = (this.visibleStartIndex + slot) % this.totalColumns;
        const colData = this.columnData[colIndex];
        const col = this.createColumn(colData);
        col.style.transform = 'translateX(0)';
        col.style.transition = 'none';
        this.wrapper.appendChild(col);
        
        // Add divider after each column except the last
        if (slot < this.visibleCount - 1) {
          const divider = this.createDivider();
          this.wrapper.appendChild(divider);
        }
      }
      
      this.lastContainerWidth = this.container.clientWidth;
    }
    
    needsRotation() {
      return this.visibleCount < this.totalColumns;
    }
    
    rotateNext() {
      if (this.isAnimating || !this.needsRotation()) return;
      this.isAnimating = true;
      
      const columns = Array.from(this.wrapper.querySelectorAll('.multicolumn-list__item--visible'));
      const dividers = Array.from(this.wrapper.querySelectorAll('.multicolumn-divider'));
      
      if (columns.length === 0) {
        this.isAnimating = false;
        return;
      }
      
      const wrapperRect = this.wrapper.getBoundingClientRect();
      const currentPositions = columns.map(col => {
        const rect = col.getBoundingClientRect();
        return rect.left - wrapperRect.left;
      });
      
      const firstCol = columns[0];
      const firstColWidth = firstCol.offsetWidth;
      
      const enteringColIndex = (this.visibleStartIndex + this.visibleCount) % this.totalColumns;
      const enteringColData = this.columnData[enteringColIndex];
      const enteringCol = this.createColumn(enteringColData);
      
      const lastCol = columns[columns.length - 1];
      const lastColRight = currentPositions[currentPositions.length - 1] + lastCol.offsetWidth;
      const enterStartX = lastColRight + 50;
      
      // Create entering divider
      const enteringDivider = this.createDivider();
      enteringDivider.style.position = 'absolute';
      enteringDivider.style.left = `${lastColRight + 25}px`;
      enteringDivider.style.top = '0';
      enteringDivider.style.height = '100%';
      this.wrapper.appendChild(enteringDivider);
      
      enteringCol.style.position = 'absolute';
      enteringCol.style.left = `${enterStartX}px`;
      enteringCol.style.top = '0';
      this.wrapper.appendChild(enteringCol);
      
      void enteringCol.offsetWidth;
      
      const gap = columns.length > 1 ? currentPositions[1] - currentPositions[0] - firstColWidth : 50;
      const slideDistance = firstColWidth + gap;
      
      requestAnimationFrame(() => {
        columns.forEach(col => col.style.transition = 'transform 0.5s ease');
        dividers.forEach(div => div.style.transition = 'transform 0.5s ease');
        enteringCol.style.transition = 'left 0.5s ease';
        enteringDivider.style.transition = 'left 0.5s ease';
        
        void this.wrapper.offsetWidth;
        
        requestAnimationFrame(() => {
          columns.forEach(col => {
            col.style.transform = `translateX(-${slideDistance}px)`;
          });
          dividers.forEach(div => {
            div.style.transform = `translateX(-${slideDistance}px)`;
          });
          enteringCol.style.left = `${currentPositions[currentPositions.length - 1]}px`;
          enteringDivider.style.left = `${currentPositions[currentPositions.length - 1] - 25}px`;
        });
      });
      
      setTimeout(() => {
        this.visibleStartIndex = (this.visibleStartIndex + 1) % this.totalColumns;
        this.render();
        this.isAnimating = false;
      }, 520);
    }
    
    startRotation() {
      if (this.rotationTimer || !this.autoplayEnabled || !this.needsRotation()) return;
      this.rotationTimer = setInterval(() => this.rotateNext(), this.rotationDelay);
    }
    
    stopRotation() {
      if (this.rotationTimer) {
        clearInterval(this.rotationTimer);
        this.rotationTimer = null;
      }
    }
    
    init() {
      if (this.totalColumns === 0) return;
      
      this.render();
      
      if (this.needsRotation() && this.autoplayEnabled) {
        this.startRotation();
      }
      
      this.container.addEventListener('mouseenter', () => this.stopRotation());
      this.container.addEventListener('mouseleave', () => {
        if (this.needsRotation() && this.autoplayEnabled) this.startRotation();
      });
    }
    
    // Called externally on resize
    reinit() {
      this.stopRotation();
      this.isAnimating = false;
      this.visibleStartIndex = 0;
      this.visibleCount = 0;
      this.render();
      if (this.needsRotation() && this.autoplayEnabled) {
        this.startRotation();
      }
    }
    
    destroy() {
      this.stopRotation();
    }
  }
  
  const instances = new Map();
  let lastWindowWidth = window.innerWidth;
  
  function initMulticolumns() {
    instances.forEach(i => i.destroy());
    instances.clear();
    document.querySelectorAll('.multicolumn-list--dynamic').forEach(c => {
      instances.set(c, new MulticolumnManager(c));
    });
    lastWindowWidth = window.innerWidth;
  }
  
  let resizeTimeout;
  window.addEventListener('resize', function() {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(function() {
      const newWidth = window.innerWidth;
      if (Math.abs(newWidth - lastWindowWidth) > 20) {
        instances.forEach(i => i.reinit());
        lastWindowWidth = newWidth;
      }
    }, 200);
  });
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMulticolumns);
  } else {
    setTimeout(initMulticolumns, 100);
  }
  
  document.addEventListener('shopify:section:load', () => setTimeout(initMulticolumns, 100));
  document.addEventListener('shopify:section:unload', (e) => {
    const c = e.target.querySelector('.multicolumn-list--dynamic');
    if (c && instances.has(c)) {
      instances.get(c).destroy();
      instances.delete(c);
    }
  });
})();
